import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useScoresNavigate } from './scoresPath';
import { Star, Bell, BellRing, ChevronLeft, ChevronRight, RefreshCw, Loader2, BarChart3, Target, Zap, ArrowRight, Flame, Tv } from 'lucide-react';
import { toast } from 'sonner';
import { useFavoriteTeams, useFavoriteMatches } from './useScoresFavorites';
import { MAJOR_LEAGUE_PRIORITY, hasLeagueTable } from './majorLeagues';
import ScoresFreeTipsCard from './ScoresFreeTipsCard';
import ScoresPredictionsAd from './ScoresPredictionsAd';
import ScoresTelegramCard from './ScoresTelegramCard';
import LeagueOverlay from './LeagueOverlay';

import BroadcasterBadges from './BroadcasterBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_LIVE = ['1H', '2H', 'ET', 'P', 'HT'];
const STATUS_FINISHED = ['FT', 'AET', 'PEN'];

/**
 * Robust JSON fetch with a 15s timeout. Returns `{ok, data}` so callers
 * can preserve their last-known-good state instead of blanking the UI
 * on a transient network blip — same pattern as the main app, fixes
 * the "No fixtures scheduled" false-empty bug the user reported.
 */
const robustFetch = async (url, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const r = await fetch(url, { signal: controller.signal });
    if (!r.ok) return { ok: false, data: null };
    return { ok: true, data: await r.json() };
  } catch {
    return { ok: false, data: null };
  } finally {
    clearTimeout(timer);
  }
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const addDays = (iso, n) => {
  const d = new Date(iso); d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const formatDateLabel = (iso) => {
  const today = todayISO();
  if (iso === today) return 'Today';
  if (iso === addDays(today, -1)) return 'Yesterday';
  if (iso === addDays(today, 1)) return 'Tomorrow';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

const getStatusDisplay = (m) => {
  const s = m.status_short;
  if (STATUS_LIVE.includes(s)) {
    if (s === 'HT') return { text: 'HT', live: true, color: 'text-amber-400' };
    return { text: `${m.elapsed ?? ''}'`, live: true, color: 'text-[var(--sk-live)]' };
  }
  if (STATUS_FINISHED.includes(s)) return { text: s, live: false, color: 'text-zinc-500' };
  if (s === 'PST') return { text: 'PPD', live: false, color: 'text-red-400' };
  if (s === 'CANC') return { text: 'CANC', live: false, color: 'text-red-400' };
  if (m.date) {
    const d = new Date(m.date);
    return { text: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }), live: false, color: 'text-zinc-400' };
  }
  return { text: 'TBD', live: false, color: 'text-zinc-600' };
};

const ScoresMatches = ({ onPredictionsCtaClick, ensurePush }) => {
  const navigate = useScoresNavigate();
  const [date, setDate] = useState(todayISO());
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLiveOnly, setShowLiveOnly] = useState(false);
  const [showTvOnly, setShowTvOnly] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const [collapsed, setCollapsed] = useState(new Set());
  const [overlayLeague, setOverlayLeague] = useState(null);
  const [overlayTab, setOverlayTab] = useState('table');
  const [fetchFailed, setFetchFailed] = useState(false);

  // Cache of league standings keyed by league_id → { teamId: position }
  const [positionMaps, setPositionMaps] = useState({});
  const fetchedLeaguesRef = useRef(new Set());

  const favTeams = useFavoriteTeams();
  const favMatches = useFavoriteMatches();

  // Which of today's fixtures have an AI Value Banker → show a badge + funnel.
  const [bankerIds, setBankerIds] = useState(new Set());
  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/bankers/today`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (active && d?.bankers) setBankerIds(new Set(d.bankers.map((b) => String(b.match_id))));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const fetchFixtures = useCallback(async (dateStr, silent = false) => {
    if (!silent) setLoading(true);
    const { ok, data } = await robustFetch(`${API_URL}/api/live-scores/fixtures?date=${dateStr}`);
    // Only overwrite leagues if the fetch genuinely succeeded with a
    // valid payload. A transient failure must NOT show "No fixtures
    // scheduled" — that's the false-empty bug we hit on production.
    if (ok && data && Array.isArray(data.leagues)) {
      setLeagues(data.leagues);
      setLiveCount(data.live_count || 0);
      setFetchFailed(false);
    } else if (!silent) {
      // First-load failure with no cached data — surface the retry UI.
      setFetchFailed(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFixtures(date); }, [date, fetchFixtures]);

  useEffect(() => {
    if (date !== todayISO()) return undefined;
    // Live-aware polling: refresh every 20s while games are in play, 60s
    // otherwise — keeps scores snappy without hammering the API off-peak.
    const hasLive = (leagues || []).some((lg) =>
      (lg.matches || []).some((m) =>
        ['1H', '2H', 'ET', 'P', 'HT', 'LIVE', 'BT', 'INT', 'SUSP'].includes(m.status_short)));
    const ms = hasLive ? 20_000 : 60_000;
    const t = setInterval(() => fetchFixtures(date, true), ms);
    return () => clearInterval(t);
  }, [date, fetchFixtures, leagues]);

  // Fetch standings for visible major-European leagues to overlay positions.
  useEffect(() => {
    leagues.forEach((lg) => {
      if (!hasLeagueTable(lg.league_id)) return;
      if (fetchedLeaguesRef.current.has(lg.league_id)) return;
      fetchedLeaguesRef.current.add(lg.league_id);
      fetch(`${API_URL}/api/standings/table/${encodeURIComponent(lg.name)}/${encodeURIComponent(lg.country || '')}`)
        .then((r) => (r.ok ? r.json() : []))
        .then((rows) => {
          const list = Array.isArray(rows) ? rows : (rows.table || rows.standings || []);
          const map = {};
          list.forEach((row) => {
            const teamId = row.team_id || row.team?.id;
            const pos = row.position ?? row.rank;
            if (teamId != null && pos != null) map[teamId] = pos;
          });
          if (Object.keys(map).length > 0) {
            setPositionMaps((prev) => ({ ...prev, [lg.league_id]: map }));
          }
        })
        .catch(() => {});
    });
  }, [leagues]);

  const toggleCollapse = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Sort: (1) leagues containing favorite teams,
  //       (2) major European leagues in priority order,
  //       (3) live in other leagues, (4) everything else.
  const displayLeagues = useMemo(() => {
    return leagues
      .map((lg) => {
        let matches = lg.matches;
        if (showLiveOnly) matches = matches.filter((m) => STATUS_LIVE.includes(m.status_short));
        if (showTvOnly) matches = matches.filter((m) => (m.broadcasters?.length || 0) > 0);
        matches = [...matches].sort((a, b) => {
          const aFav = favTeams.isFavorite(a.home_id) || favTeams.isFavorite(a.away_id);
          const bFav = favTeams.isFavorite(b.home_id) || favTeams.isFavorite(b.away_id);
          if (aFav !== bFav) return aFav ? -1 : 1;
          return (a.timestamp || 0) - (b.timestamp || 0);
        });
        return { ...lg, matches };
      })
      .filter((lg) => lg.matches.length > 0)
      .sort((a, b) => {
        const aHasFav = a.matches.some((m) => favTeams.isFavorite(m.home_id) || favTeams.isFavorite(m.away_id));
        const bHasFav = b.matches.some((m) => favTeams.isFavorite(m.home_id) || favTeams.isFavorite(m.away_id));
        if (aHasFav !== bHasFav) return aHasFav ? -1 : 1;

        const aMajor = MAJOR_LEAGUE_PRIORITY[a.league_id];
        const bMajor = MAJOR_LEAGUE_PRIORITY[b.league_id];
        if (aMajor != null && bMajor != null) return aMajor - bMajor;
        if (aMajor != null) return -1;
        if (bMajor != null) return 1;

        const aLive = a.matches.some((m) => STATUS_LIVE.includes(m.status_short));
        const bLive = b.matches.some((m) => STATUS_LIVE.includes(m.status_short));
        if (aLive !== bLive) return aLive ? -1 : 1;

        return (a.country || '').localeCompare(b.country || '');
      });
  }, [leagues, showLiveOnly, showTvOnly, favTeams]);

  return (
    <div className="pb-4" data-testid="scores-matches-page">
      {/* SmartKick Predictions house-ad — dismissible, rotates creatives. */}
      <ScoresPredictionsAd onCtaClick={onPredictionsCtaClick} />

      {/* Free Tips Card */}
      <ScoresFreeTipsCard onCtaClick={onPredictionsCtaClick} />

      {/* Free Telegram funnel — daily banker (sits after the free tips) */}
      <ScoresTelegramCard />

      {/* Sticky filters */}
      <div className="sticky top-14 z-30 bg-[var(--sk-bg)] border-b border-[var(--sk-border)]">
        <div className="flex items-center justify-between px-4 py-2.5 gap-2">
          <button onClick={() => setDate(addDays(date, -1))} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="date-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setDate(todayISO())} className="flex-1 text-center text-[13px] font-bold" data-testid="date-label">{formatDateLabel(date)}</button>
          <button onClick={() => setDate(addDays(date, 1))} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="date-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-2 px-4 pb-2.5">
          <button onClick={() => { setShowLiveOnly(false); setShowTvOnly(false); }} data-testid="filter-all" className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${!showLiveOnly && !showTvOnly ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'}`}>All Matches</button>
          <button onClick={() => { setShowLiveOnly(true); setShowTvOnly(false); }} data-testid="filter-live" className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-1.5 transition-colors ${showLiveOnly ? 'bg-[var(--sk-live)] text-white' : 'bg-white/5 text-zinc-400'}`}>
            <span className="live-dot"></span>
            Live {liveCount > 0 && <span className="opacity-80">({liveCount})</span>}
          </button>
          <button onClick={() => { setShowTvOnly(true); setShowLiveOnly(false); }} data-testid="filter-tv" className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-1.5 transition-colors ${showTvOnly ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'}`}>
            <Tv className="w-3 h-3" /> On TV
          </button>
          <button onClick={() => fetchFixtures(date)} className="shrink-0 ml-auto w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Leagues */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[var(--sk-accent)] animate-spin" /></div>
      ) : fetchFailed && displayLeagues.length === 0 ? (
        <div className="text-center py-16 px-4" data-testid="scores-fixtures-error">
          <RefreshCw className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-300 text-sm font-bold mb-1">Couldn't load fixtures</p>
          <p className="text-zinc-500 text-xs mb-4">Check your connection and try again.</p>
          <button
            onClick={() => { setFetchFailed(false); fetchFixtures(date); }}
            className="px-5 py-2 bg-[var(--sk-accent)] hover:opacity-90 rounded-full text-xs font-bold text-black transition-opacity"
            data-testid="scores-fixtures-retry"
          >
            Retry
          </button>
        </div>
      ) : displayLeagues.length === 0 ? (
        <div className="text-center py-20 px-4 text-zinc-500 text-sm">
          {showTvOnly ? 'No televised games on this date. Games appear here once they\'re tagged as on TV.' : showLiveOnly ? 'No live matches right now.' : `No fixtures scheduled for ${formatDateLabel(date)}.`}
        </div>
      ) : (
        <div>
          {displayLeagues.map((lg, idx) => {
            const key = `${lg.league_id}_${lg.name}`;
            const isCollapsed = collapsed.has(key);
            const positions = positionMaps[lg.league_id] || {};
            // Insert the inline AI promo every 3 leagues for funnel pressure.
            const showInlinePromo = idx > 0 && idx % 3 === 0;
            return (
              <div key={key}>
                {showInlinePromo && <InlinePredictionsPromo onClick={onPredictionsCtaClick} />}
                <div className="bg-[var(--sk-surface)]/40 border-t border-[var(--sk-border)]">
                  <div className="flex items-center gap-2 px-4 py-2.5">
                    <button
                      onClick={() => toggleCollapse(key)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-90"
                      data-testid={`league-header-${lg.league_id}`}
                    >
                      {lg.logo && <img src={lg.logo} alt="" className="w-5 h-5 object-contain shrink-0" loading="lazy" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-bold text-white truncate">{lg.name}</div>
                        <div className="text-[10px] text-zinc-500 truncate">{lg.country}</div>
                      </div>
                      <span className="text-[10px] text-zinc-500">{lg.matches.length}</span>
                      <ChevronRight className={`w-4 h-4 text-zinc-600 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
                    </button>
                    {/* Table & Stats buttons for major leagues with a table */}
                    {hasLeagueTable(lg.league_id) && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setOverlayLeague(lg); setOverlayTab('table'); }}
                          className="px-2 py-1 rounded-md bg-accent-soft text-accent text-[10px] font-bold flex items-center gap-1 hover:bg-accent-soft/80"
                          data-testid={`league-table-${lg.league_id}`}
                        >
                          <BarChart3 className="w-3 h-3" /> Table
                        </button>
                        <button
                          onClick={() => { setOverlayLeague(lg); setOverlayTab('stats'); }}
                          className="px-2 py-1 rounded-md bg-amber-500/15 text-amber-400 text-[10px] font-bold flex items-center gap-1 hover:bg-amber-500/25"
                          data-testid={`league-stats-${lg.league_id}`}
                        >
                          <Target className="w-3 h-3" /> Stats
                        </button>
                      </div>
                    )}
                  </div>
                  {!isCollapsed && (
                    <div>
                      {lg.matches.map((m) => (
                        <MatchRow
                          key={m.fixture_id}
                          match={m}
                          favTeams={favTeams}
                          favMatches={favMatches}
                          positions={positions}
                          ensurePush={ensurePush}
                          isBanker={bankerIds.has(String(m.fixture_id))}
                          onClick={() => m.fixture_id && navigate(`/match/${m.fixture_id}`)}
                          onOpenTeam={(teamId) => teamId && navigate(`/team/${teamId}`)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LeagueOverlay
        open={!!overlayLeague}
        league={overlayLeague}
        initialTab={overlayTab}
        onClose={() => setOverlayLeague(null)}
      />
    </div>
  );
};

// ============== Inline AI Promo (between leagues) ==============
const InlinePredictionsPromo = ({ onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 bg-emerald-500/8 border-y border-emerald-500/25 hover:bg-emerald-500/15 transition-colors text-left"
    data-testid="inline-predictions-promo"
  >
    <div className="shrink-0 w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
      <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[12px] font-extrabold text-emerald-400 truncate">
        SmartKick AI predicts winners for these matches
      </div>
      <div className="text-[10px] text-zinc-400 truncate">Get score + BTTS + confidence for every fixture</div>
    </div>
    <span className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold">
      Try Free <ArrowRight className="w-3 h-3" />
    </span>
  </button>
);

// ============== Match Row ==============
const MatchRow = ({ match: m, favTeams, favMatches, positions, ensurePush, isBanker, onClick, onOpenTeam }) => {
  const status = getStatusDisplay(m);
  const isFinished = STATUS_FINISHED.includes(m.status_short);
  const homeFav = favTeams.isFavorite(m.home_id);
  const awayFav = favTeams.isFavorite(m.away_id);
  const notifyOn = favMatches.isFavorite(m.fixture_id);

  const toggleNotify = (e) => {
    e.stopPropagation();
    favMatches.toggle({
      fixture_id: m.fixture_id,
      home_team: m.home_team,
      away_team: m.away_team,
      home_logo: m.home_logo,
      away_logo: m.away_logo,
      date: m.date,
      status_short: m.status_short,
    });
    if (!notifyOn) {
      // On native, make sure the device is registered for push (no-op on web).
      // This re-triggers the APNs permission/registration if it wasn't granted
      // at launch, so enabling an alert actually delivers.
      if (typeof ensurePush === 'function') ensurePush();
      // Web browsers: request the Web Notification permission.
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
      toast.success('Alerts on', { description: 'We\'ll notify you on goals & key moments.' });
    } else {
      toast('Alerts off');
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      className="match-row w-full flex items-center gap-3 px-4 py-2.5 border-t border-[var(--sk-border)]/60 text-left hover:bg-white/[0.02] transition-colors cursor-pointer"
      data-testid={`match-${m.fixture_id}`}
    >
      <div className="w-12 shrink-0 text-center">
        {status.live && <span className="live-dot inline-block mb-1"></span>}
        <div className={`text-[11px] font-bold ${status.color} leading-tight`}>{status.text}</div>
      </div>
      <div className="flex-1 min-w-0">
        <TeamLine
          team={{ id: m.home_id, name: m.home_team, logo: m.home_logo }}
          position={positions[m.home_id]}
          goals={m.home_goals}
          reds={m.home_red}
          finished={isFinished}
          isFav={homeFav}
          onToggleFav={() => favTeams.toggle({ id: m.home_id, name: m.home_team, logo: m.home_logo })}
          onOpenProfile={() => onOpenTeam?.(m.home_id)}
        />
        <TeamLine
          team={{ id: m.away_id, name: m.away_team, logo: m.away_logo }}
          position={positions[m.away_id]}
          goals={m.away_goals}
          reds={m.away_red}
          finished={isFinished}
          isFav={awayFav}
          onToggleFav={() => favTeams.toggle({ id: m.away_id, name: m.away_team, logo: m.away_logo })}
          onOpenProfile={() => onOpenTeam?.(m.away_id)}
        />
        {m.broadcasters?.length > 0 && (
          <div className="mt-1">
            <BroadcasterBadges broadcasters={m.broadcasters} size="sm" />
          </div>
        )}
        {isBanker && (
          <button
            onClick={(e) => { e.stopPropagation(); window.location.assign('/app/bankers'); }}
            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold hover:bg-emerald-500/25 transition-colors"
            data-testid={`banker-badge-${m.fixture_id}`}
          >
            <Flame className="w-3 h-3" /> Value Banker
          </button>
        )}
      </div>
      <button
        onClick={toggleNotify}
        data-testid={`notify-${m.fixture_id}`}
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${notifyOn ? 'bg-accent-soft text-accent' : 'bg-white/5 text-zinc-500 hover:text-zinc-300'}`}
        aria-label={notifyOn ? 'Disable alerts' : 'Enable alerts'}
      >
        {notifyOn ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
      </button>
    </div>
  );
};

// ============== Team Line ==============
const TeamLine = ({ team, position, goals, reds, finished, isFav, onToggleFav, onOpenProfile }) => (
  <div className="flex items-center gap-2 py-0.5">
    {team.logo && <img src={team.logo} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />}
    {position != null && (
      <span className="text-[9px] font-bold text-zinc-500 tabular-nums w-3 text-center shrink-0">{position}</span>
    )}
    <button
      onClick={(e) => { e.stopPropagation(); onOpenProfile?.(); }}
      className={`text-left text-[13px] truncate hover:text-accent ${finished ? 'text-zinc-400' : 'text-white'}`}
      data-testid={`team-name-${team.id}`}
    >
      {team.name}
    </button>
    {reds > 0 && (
      <span
        className="shrink-0 inline-flex items-center gap-0.5"
        title={`${reds} red card${reds > 1 ? 's' : ''}`}
        data-testid={`red-card-${team.id}`}
        aria-label={`${reds} red card${reds > 1 ? 's' : ''}`}
      >
        <span className="inline-block w-[11px] h-[15px] rounded-[2px] bg-[#e11d48] shadow-[0_0_4px_rgba(225,29,72,0.6)]" />
        {reds > 1 && <span className="text-[10px] font-bold text-[#e11d48]">{reds}</span>}
      </span>
    )}
    <span className="flex-1" />
    <button
      onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
      className="w-5 h-5 flex items-center justify-center opacity-60 hover:opacity-100"
      aria-label={isFav ? `Unfavorite ${team.name}` : `Favorite ${team.name}`}
      data-testid={`fav-team-${team.id}`}
    >
      <Star className={`w-3 h-3 ${isFav ? 'text-[var(--sk-accent)]' : 'text-zinc-600'}`} fill={isFav ? 'currentColor' : 'none'} />
    </button>
    <span className={`w-5 text-right text-[14px] font-bold tabular-nums ${finished ? 'text-zinc-400' : 'text-white'}`}>
      {goals != null ? goals : ''}
    </span>
  </div>
);

export default ScoresMatches;
